import React, { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../services/adminApi';
import { InviteRecord, AdminOrgListItem } from '../../types';
import { Plus, CheckCircle2, Trash2, Link as LinkIcon, Building2, UserPlus, X, Copy, Mail } from 'lucide-react';

export function AdminPortalInvites() {
  const [invites, setInvites] = useState<InviteRecord[]>([]);
  const [orgs, setOrgs] = useState<AdminOrgListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [newInviteUrl, setNewInviteUrl] = useState<{ url: string; email: string | null } | null>(null);

  const [form, setForm] = useState<{
    invite_type: 'new_org' | 'join_org';
    email: string;
    organization_id: string;
    role: 'owner' | 'manager' | 'cashier';
    suggested_subdomain: string;
    expires_in_days: number;
  }>({
    invite_type: 'new_org',
    email: '',
    organization_id: '',
    role: 'cashier',
    suggested_subdomain: '',
    expires_in_days: 14,
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [{ invites }, { organizations }] = await Promise.all([
        adminApi.listInvites(),
        adminApi.listOrganizations(),
      ]);
      setInvites(invites);
      setOrgs(organizations);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCreating(true);
    try {
      const payload: any = {
        invite_type: form.invite_type,
        expires_in_days: form.expires_in_days,
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.invite_type === 'new_org' && form.suggested_subdomain.trim()) {
        payload.suggested_subdomain = form.suggested_subdomain.trim().toLowerCase();
      }
      if (form.invite_type === 'join_org') {
        if (!form.organization_id) throw new Error('Pick an organization.');
        payload.organization_id = form.organization_id;
        payload.role = form.role;
      }
      const result = await adminApi.createInvite(payload);
      setNewInviteUrl({ url: result.redemption_url, email: payload.email || null });
      setShowForm(false);
      setForm({ ...form, email: '', organization_id: '', suggested_subdomain: '' });
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm('Revoke this invite? The link will stop working immediately.')) return;
    setError('');
    try {
      await adminApi.revokeInvite(id);
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCopy = async (token: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      setError('Could not copy to clipboard.');
    }
  };

  const sortedInvites = useMemo(() => {
    return [...invites].sort((a, b) => b.created_at - a.created_at);
  }, [invites]);

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400 text-lg">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto pb-24 p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Invites</h1>
          <p className="text-slate-500 font-medium mt-1">Generate redemption links for new tenants or new teammates.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-5 py-3.5 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition-all active:scale-95 shadow-md"
        >
          <Plus className="w-5 h-5" />
          New Invite
        </button>
      </div>

      {error && (
        <div className="px-5 py-4 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-6 font-medium">
          {error}
        </div>
      )}

      {newInviteUrl && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-[2rem] p-6 mb-6 relative">
          <button
            onClick={() => setNewInviteUrl(null)}
            className="absolute top-4 right-4 p-1.5 text-emerald-400 hover:text-emerald-700 rounded-xl hover:bg-emerald-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-3 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-bold text-emerald-800 text-[15px]">Invite created — share this link</div>
              {!newInviteUrl.email && (
                <div className="text-[13px] text-emerald-600 mt-1 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  Email delivery not yet configured — send this URL manually.
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white border border-emerald-200 rounded-2xl px-4 py-3">
            <code className="flex-1 text-[13px] text-slate-700 font-mono break-all">{newInviteUrl.url}</code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(newInviteUrl.url);
                setCopiedToken('new');
                setTimeout(() => setCopiedToken(null), 2000);
              }}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-xl text-[13px] font-bold hover:bg-emerald-700 transition-colors"
            >
              {copiedToken === 'new' ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copiedToken === 'new' ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-white p-8 rounded-[2rem] mb-8 shadow-xl shadow-slate-200/40 border border-slate-200/60">
          <h2 className="text-xl font-bold text-slate-800 mb-6">New Invite</h2>
          <form onSubmit={handleCreate} className="flex flex-col gap-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TypeOption
                active={form.invite_type === 'new_org'}
                onClick={() => setForm({ ...form, invite_type: 'new_org' })}
                icon={<Building2 className="w-5 h-5" />}
                title="New Workspace"
                desc="Redeemer creates a brand-new workspace and becomes its owner."
              />
              <TypeOption
                active={form.invite_type === 'join_org'}
                onClick={() => setForm({ ...form, invite_type: 'join_org' })}
                icon={<UserPlus className="w-5 h-5" />}
                title="Join Workspace"
                desc="Redeemer joins an existing workspace with a role you choose."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Email (optional)">
                <input
                  className={inputCls}
                  type="email"
                  placeholder="Lock to this email, or leave blank"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                />
              </Field>
              <Field label="Expires in (days)">
                <input
                  className={inputCls}
                  type="number"
                  min={1}
                  max={90}
                  value={form.expires_in_days}
                  onChange={e => setForm({ ...form, expires_in_days: parseInt(e.target.value) || 14 })}
                />
              </Field>

              {form.invite_type === 'new_org' && (
                <Field label="Suggested Subdomain (optional)">
                  <div className="flex items-stretch h-14 relative">
                    <input
                      type="text"
                      placeholder="saltwater"
                      value={form.suggested_subdomain}
                      onChange={e => setForm({ ...form, suggested_subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      className="flex-1 px-5 bg-white border border-slate-200 border-r-0 rounded-l-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-slate-900 transition-all"
                    />
                    <span className="px-5 flex items-center bg-slate-50 border border-slate-200 border-l-0 rounded-r-2xl text-[15px] font-medium text-slate-400 whitespace-nowrap">
                      .taplo.cc
                    </span>
                  </div>
                </Field>
              )}

              {form.invite_type === 'join_org' && (
                <>
                  <Field label="Workspace *">
                    <select
                      className={inputCls}
                      value={form.organization_id}
                      onChange={e => setForm({ ...form, organization_id: e.target.value })}
                      required
                    >
                      <option value="">Select a workspace…</option>
                      {orgs.map(o => (
                        <option key={o.id} value={o.id}>{o.name} ({o.subdomain}.taplo.cc)</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Role *">
                    <select
                      className={inputCls}
                      value={form.role}
                      onChange={e => setForm({ ...form, role: e.target.value as any })}
                    >
                      <option value="cashier">Cashier — operates POS</option>
                      <option value="manager">Manager — manages products</option>
                      <option value="owner">Owner — full access</option>
                    </select>
                  </Field>
                </>
              )}
            </div>

            <div className="flex gap-3 mt-2">
              <button type="submit" disabled={creating} className="px-6 py-3.5 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition-all active:scale-95 disabled:opacity-50">
                {creating ? 'Generating…' : 'Generate Invite Link'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setError(''); }} className="px-6 py-3.5 bg-white text-slate-600 border border-slate-200 rounded-2xl font-bold hover:bg-slate-50 transition-all active:scale-95">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Type</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Target</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Email</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Status</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Link</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedInvites.map((inv) => {
                const used = !!inv.used_at;
                const expired = !used && inv.expires_at < Date.now();
                const active = !used && !expired;
                return (
                  <tr key={inv.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/30">
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide border bg-slate-50 text-slate-700 border-slate-200">
                        {inv.invite_type === 'new_org' ? <Building2 className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}
                        {inv.invite_type === 'new_org' ? 'New Workspace' : 'Join'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[14px] font-medium text-slate-600">
                      {inv.invite_type === 'new_org'
                        ? (inv.suggested_subdomain ? `${inv.suggested_subdomain}.taplo.cc` : '—')
                        : (
                          <>
                            <div className="font-bold text-slate-800">{inv.organization_name}</div>
                            <div className="text-[12px] text-slate-400 uppercase tracking-wide">{inv.role}</div>
                          </>
                        )}
                    </td>
                    <td className="px-6 py-4 text-[14px] text-slate-500">{inv.email || '—'}</td>
                    <td className="px-6 py-4">
                      {used ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-500 border border-slate-200 uppercase tracking-wide">
                          <CheckCircle2 className="w-3 h-3" /> Used
                        </span>
                      ) : expired ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-red-50 text-red-600 border border-red-200 uppercase tracking-wide">
                          Expired
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wide">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {active && inv.redemption_url ? (
                        <button
                          onClick={() => handleCopy(inv.token, inv.redemption_url!)}
                          className="inline-flex items-center gap-1.5 text-[#0f766e] hover:text-[#0f3d3a] font-bold text-[13px]"
                        >
                          {copiedToken === inv.token ? <CheckCircle2 className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}
                          {copiedToken === inv.token ? 'Copied!' : 'Copy link'}
                        </button>
                      ) : (
                        <span className="text-slate-300 text-[13px]">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {active && (
                        <button
                          onClick={() => handleRevoke(inv.id)}
                          className="inline-flex items-center justify-center p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                          title="Revoke"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {sortedInvites.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-slate-400 font-medium">
                    No invites yet. Click <strong>New Invite</strong> to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TypeOption({
  active,
  onClick,
  icon,
  title,
  desc,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left p-4 rounded-2xl border-2 transition-all ${
        active
          ? 'bg-slate-900 text-white border-slate-900 shadow-md'
          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
      }`}
    >
      <div className="flex items-center gap-2 font-bold text-[15px] mb-1">
        {icon}
        {title}
      </div>
      <div className={`text-[13px] font-medium ${active ? 'text-slate-300' : 'text-slate-500'}`}>
        {desc}
      </div>
    </button>
  );
}

const inputCls = 'w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-all placeholder-slate-300';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">
        {label}
      </label>
      {children}
    </div>
  );
}
